import React, { useContext, useState } from 'react';

import { Form, Formik } from 'formik';

import Button from '@components/common/Button/Button';
import Checkbox from '@components/common/Formik/FormikCheckBox';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Radio from '@components/common/Radio/Radio';
import RadioGroup from '@components/common/RadioGroup/RadioGroup';
import Text from '@components/common/Text/Text';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import { potPrivacyType } from '@propTypes/potTypes';
import potService from '@services/domain/Lpc/PotService';

import css from './Confidentiality.module.scss';

type ConfidentialityProps = {
    onCloseModal: () => void;
    potRef: string;
    privacy: potPrivacyType;
};

const Confidentiality = ({ potRef, privacy, onCloseModal }: ConfidentialityProps) => {
    const { t } = useTranslateLpc(['lpc-pot']);

    const potContext = useContext(PotContext);
    const { getUpdatedPot } = potContext;

    const [isLoading, setIsLoading] = useState(false);

    const getInitialValues = () => {
        return {
            showTotalContribution: privacy.showTotalContribution,
            showParticipantName: privacy.showParticipantName,
            showContributionAmount: privacy.showContributionAmount,
            showParticipantsList: privacy.showParticipantsList,
            askParticipantsForAddress: privacy.askParticipantsForAddress,
        };
    };

    const onSubmit = (values: {
        showTotalContribution: boolean;
        showParticipantName: boolean;
        showContributionAmount: boolean;
        showParticipantsList: boolean;
        askParticipantsForAddress: boolean;
    }) => {
        setIsLoading(true);

        potService
            .updatePotSettingsConfidentiality(potRef, values)
            .then((res) => res.status === 200 && getUpdatedPot(potRef))
            .then(() => onCloseModal())
            .catch((err) => err)
            .finally(() => setIsLoading(false));
    };

    const onCancel = () => {
        onCloseModal();
    };

    return (
        <Formik initialValues={getInitialValues()} onSubmit={(values) => onSubmit(values)}>
            {({ setFieldValue }) => (
                <Form>
                    <>
                        <Text variant={'caption_00'} color={'txt-primary'}>
                            {t('settings.confidentiality.amount-title')}
                        </Text>
                        <RadioGroup customClass={css.radio__group} name="showTotalContribution" hideErrors>
                            <div className={css.form__input}>
                                <Radio
                                    inputValue={true}
                                    inputName="showTotalContribution"
                                    label={t('settings.confidentiality.amount-all-label')}
                                    desc={t('settings.confidentiality.amount-all-desc')}
                                    onChange={(value) => {
                                        setFieldValue('showTotalContribution', value);
                                    }}
                                    customClass={css.form__input__label}
                                    containerClass={css.form__input__desc}
                                />
                            </div>
                            <div className={css.form__input}>
                                <Radio
                                    inputValue={false}
                                    inputName="showTotalContribution"
                                    label={t('settings.confidentiality.amount-organiser-label')}
                                    desc={t('settings.confidentiality.amount-organiser-desc')}
                                    onChange={(value) => {
                                        setFieldValue('showTotalContribution', value);
                                    }}
                                    customClass={css.form__input__label}
                                    containerClass={css.form__input__desc}
                                />
                            </div>
                        </RadioGroup>

                        <div className={css.form__divider} />

                        <Text variant={'caption_00'} color={'txt-primary'}>
                            {t('settings.confidentiality.display-title')}
                        </Text>

                        <div className={css.form__input}>
                            <Checkbox name="showParticipantName">
                                {t('settings.confidentiality.display-participant-id')}
                            </Checkbox>
                        </div>
                        <div className={css.form__input}>
                            <Checkbox name="showContributionAmount">
                                {t('settings.confidentiality.display-participation-amount')}
                            </Checkbox>
                        </div>
                        <div className={css.form__input}>
                            <Checkbox name="showParticipantsList">
                                {t('settings.confidentiality.display-participant-list')}
                            </Checkbox>
                        </div>

                        <div className={css.form__input}>
                            <Checkbox name="askParticipantsForAddress">
                                {t('settings.confidentiality.ask-participants-for-address')}
                            </Checkbox>
                        </div>

                        {/* SUBMIT */}
                        <div className={css.form__submit}>
                            <Button
                                type={'button'}
                                customClass={css.form__submit__cta}
                                variant={'secondary'}
                                onClick={() => onCancel()}
                            >
                                {t('settings.cancel')}
                            </Button>
                            <Button
                                type={'submit'}
                                margin={'0 0 0 8px'}
                                variant={'primary'}
                                customClass={css.form__submit__cta}
                                isLoading={isLoading}
                                isDisabled={isLoading}
                            >
                                {t('settings.confirm')}
                            </Button>
                        </div>
                    </>
                </Form>
            )}
        </Formik>
    );
};
export default Confidentiality;
